import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChange, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Observable, forkJoin, of } from 'rxjs';
import {
  ARConsoleFormatType,
  ARConsoleModulationType,
  ARConsoleType,
  ARConsoleVersion,
} from '../../../../models/agrorendement-common-lists.model';
import { ARConsole, ARVraData } from '../../../../models/agrorendement-common.model';
import { Parcel } from '../../../../models/parcel.model';
import { CapitalizePipe } from '../../../../pipes/capitalize.pipe';
import { ConsoleFormatTypeLabelPipe } from '../../../../pipes/consoleFormatTypeLabel.pipe';
import { ConsoleTypeLabelPipe } from '../../../../pipes/consoleTypeLabel.pipe';
import { ConsoleVersionFilterPipe } from '../../../../pipes/consoleVersionFilter.pipe';
import { ARCommonListsService, ARCommonListType } from '../../../../services/agrorendement-common-lists.service';
import { ARConsoleService } from '../../../../services/agrorendement-common.service';
// FIXME mettre ce controle independant de AR
import { I18nTranslator } from '../../../../i18n/translate';
import { ModalService } from '../../../../services/modal.service';
import { ZSNListsService, ZSNListType } from '../../../../services/zsn-lists.service';
import { showErrorMessage, sortingNumber, sortingString } from '../../../../utils/utils';
import { TranslatePipe, TranslateWithPunctuationPipe } from '../../../../pipes/translate.pipe';
import { FormsModule } from '@angular/forms';

@Component({
  standalone: true,
  selector: 'ar-vra-edit',
  templateUrl: 'vra-edit.component.html',
  imports: [RouterLink, RouterLinkActive, RouterOutlet, CommonModule, CapitalizePipe, ConsoleVersionFilterPipe, ConsoleTypeLabelPipe, ConsoleFormatTypeLabelPipe, TranslatePipe, TranslateWithPunctuationPipe, FormsModule],
  providers: [ARConsoleService],
})
export class ARVraEditComponent implements OnInit, OnChanges {
  @Input() vraData: ARVraData;
  @Input() parcel: Parcel;
  @Input() editable: boolean = true;
  @Input() modulationType: string;
  @Input() isBatch: boolean;
  @Input() cbBatchEdit: Object = {};
  @Input() isOnlyConsole: boolean = false;
  @Input() isForceSuggestConsole: boolean = false;
  @Output() modulationTypeChange = new EventEmitter();
  @Output() change = new EventEmitter();
  @Input() required: boolean = true;
  error: any;

  consoleModulationTypes: ARConsoleModulationType[];
  consoleFormatTypes: ARConsoleFormatType[];
  consoleTypes: ARConsoleType[];
  consoleVersions: ARConsoleVersion[];
  consoleModulationTypesIndex = new Map<number, ARConsoleModulationType>();
  consoleVersionsIndex = new Map<number, ARConsoleVersion>();

  areListsFetched = false;
  requireConsoleInfo = false;
  showOtherConsoleVersion = false;

  suggestConsole: ARConsole;
  // indique que la console du controle est une console suggeree
  isSuggestedConsoleSet: boolean = false;

  previousModulationTypeCode: string = null;

  constructor(
    private commonListService: ARCommonListsService,
    private zsnListService: ZSNListsService,
    private consoleService: ARConsoleService,
    private i18nTranslator: I18nTranslator,
    private modal: ModalService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  getLists() {
    const listTypes = [
      ARCommonListType.CONSOLE_FORMAT_TYPES,
      ARCommonListType.CONSOLE_TYPES,
      ARCommonListType.CONSOLE_VERSIONS,
    ];
    if (!this.modulationType || this.modulationType === 'AR') {
      listTypes.push(ARCommonListType.CONSOLE_MODULATION_TYPES);
      const obs1 = this.commonListService.getListTypes(ARCommonListType, ...listTypes);
      // pour garder le meme type de retour que pour dessous
      return forkJoin([obs1]);
    } else if (this.modulationType === 'ZSN') {
      const obs1 = this.commonListService.getListTypes(ARCommonListType, ...listTypes);
      // TODO mettre les valeurs ZSN_CONSOLE_MODULATION_TYPES dans CommonList et filtrer ?
      const obs2 = this.zsnListService.getListTypes(ZSNListType, ZSNListType.ZSN_CONSOLE_MODULATION_TYPES);
      return forkJoin([obs1, obs2]);
    }

    return of(null);
  }

  handleLists(aLists: any[]) {
    // TODO meileur facon de gerer ca ?
    this.areListsFetched = true;

    if (aLists.length === 1) {
      this.consoleModulationTypes = aLists[0].get(ARCommonListType.CONSOLE_MODULATION_TYPES).sort(sortingNumber('id'));
    } else {
      this.consoleModulationTypes = aLists[1].get(ZSNListType.ZSN_CONSOLE_MODULATION_TYPES).sort(sortingNumber('id'));
    }

    for (const p of this.consoleModulationTypes) {
      this.consoleModulationTypesIndex.set(p.id, p);
    }
    this.consoleFormatTypes = aLists[0].get(ARCommonListType.CONSOLE_FORMAT_TYPES).sort(sortingString('label'));
    this.consoleTypes = aLists[0].get(ARCommonListType.CONSOLE_TYPES).sort(sortingString('label'));
    this.consoleVersions = <ARConsoleVersion[]>(
      aLists[0].get(ARCommonListType.CONSOLE_VERSIONS).sort(sortingString('label'))
    );
    for (const v of this.consoleVersions) {
      this.consoleVersionsIndex.set(v.id, v);
    }
  }

  private getLastConsole() {
    // TODO handle error
    if (!this.parcel) {
      return of(undefined);
    }
    return this.consoleService.getLast(this.parcel.farm.id);
  }

  ngOnInit() {
    // vra data not set a ce ce moment
    this.prepareVraData();
  }

  ngOnChanges(changes: SimpleChanges) {
    const vraDataChange: SimpleChange = changes['vraData'];

    // au cas ou les listes ait ete recuperees avant
    if (vraDataChange && vraDataChange.isFirstChange()) {
      // on suppose que modualtionType est sette en meme temps
      // TODO traiter si pas le cas ?
      const obs1: Observable<any> = this.getLists();
      obs1.subscribe((data: any) => {
        this.handleLists(data);
        // vradata set et traite correctement
        this.prepareVraData();
      });
    }

    const parcelChange: SimpleChange = changes['parcel'];
    if (parcelChange && parcelChange.isFirstChange()) {
      this.getLastConsole().subscribe((data: ARConsole) => {
        if (data) {
          this.suggestConsole = data;
          if (this.isForceSuggestConsole) {
            // suppose que le resultat de cette requete sera apres le setting
            // des donnees du ngModel
            // ne change rien si donnees console deja settees
            if (!this.vraData.consoleType || !this.vraData.consoleType.id) {
              this.setSuggestedConsole();
            }
          }
        }
      });
    }

    this.change.emit(this.vraData);
  }

  prepareVraData() {
    if (!this.vraData.consoleFormatType) {
      this.vraData.consoleFormatType = new ARConsoleFormatType();
      this.vraData.consoleFormatType.id = null;
    }

    if (!this.vraData.consoleVersion) {
      this.vraData.consoleVersion = new ARConsoleVersion();
      this.vraData.consoleVersion.id = null;
    } else {
      this.onChangeConsoleVersion(this.vraData.consoleVersion.id, true);
    }

    if (!this.vraData.consoleType) {
      this.vraData.consoleType = new ARConsoleType();
      this.vraData.consoleType.id = null;
    }

    if (!this.vraData.consoleModulationType) {
      this.vraData.consoleModulationType = new ARConsoleModulationType();
      this.vraData.consoleModulationType.id = null;
    } else {
      this.onChangeConsoleModulationType(this.vraData.consoleModulationType.id, true);
    }

    if (!this.vraData.otherConsoleVersion) {
      this.vraData.otherConsoleVersion = null;
    }
  }

  onChangeConsoleModulationType(consoleModulationTypeId: any, isInit = false) {
    if (!consoleModulationTypeId) {
      return;
    }
    if (!this.areListsFetched) {
      // besoin des listes : si pas present =>
      // refait quand listes pretes
      return;
    }

    consoleModulationTypeId = +consoleModulationTypeId;

    this.modulationTypeChange.emit(this.consoleModulationTypesIndex.get(consoleModulationTypeId));

    const consoleModulationType = this.consoleModulationTypesIndex.get(consoleModulationTypeId);
    this.requireConsoleInfo = consoleModulationType.code !== 'NONE';
    if (!this.requireConsoleInfo) {
      this.vraData.consoleFormatType.id = null;
      this.vraData.consoleVersion.id = null;
      this.vraData.otherConsoleVersion = null;
      this.vraData.consoleType.id = null;
      this.previousModulationTypeCode = 'NONE';
    } else if (!isInit) {
      if (this.suggestConsole) {
        if (!this.previousModulationTypeCode || this.previousModulationTypeCode == 'NONE') {
          this.setSuggestedConsole();
        }
        this.previousModulationTypeCode = consoleModulationType.code;
      }
    } else {
      this.previousModulationTypeCode = consoleModulationType.code;
    }
  }

  setSuggestedConsole() {
    if (this.suggestConsole) {
      this.vraData.consoleType.id = this.suggestConsole.consoleType.id;
      // TODO suppr ce test ? fait quand colza et orge n'avaient pas la version de console
      if (this.suggestConsole.consoleVersion) {
        this.vraData.consoleVersion.id = this.suggestConsole.consoleVersion.id;
        this.onChangeConsoleVersion(this.vraData.consoleVersion.id);

        this.vraData.otherConsoleVersion = this.suggestConsole.otherConsoleVersion;
        if (!this.vraData.otherConsoleVersion) {
          this.vraData.otherConsoleVersion = null;
        }
        this.vraData.consoleFormatType.id = this.suggestConsole.consoleFormatType.id;
      }

      this.isSuggestedConsoleSet = true;
    }
  }

  onChangeConsoleType(consoleTypeId: any) {
    this.vraData.consoleVersion.id = null;
    this.vraData.otherConsoleVersion = null;
    this.vraData.consoleFormatType.id = null;

    if (consoleTypeId) {
      consoleTypeId = +consoleTypeId;
      const filtered = this.consoleVersions.filter((consoleVersion) => consoleVersion.consoleType.id === consoleTypeId);
      if (filtered.length === 1) {
        this.vraData.consoleVersion.id = filtered[0].id;
        this.onChangeConsoleVersion(this.vraData.consoleVersion.id);
      }
    }

    this.isSuggestedConsoleSet = false;
  }

  onChangeConsoleVersion(consoleVersionId: any, isInit = false) {
    if (!consoleVersionId) {
      return;
    }
    if (!this.areListsFetched) {
      // besoin des listes : si pas present =>
      // refait quand listes pretes
      return;
    }
    consoleVersionId = +consoleVersionId;
    const consoleVersion = this.consoleVersionsIndex.get(consoleVersionId);
    if (consoleVersion.code === 'OTHER') {
      this.showOtherConsoleVersion = true;
    } else {
      this.vraData.otherConsoleVersion = null;
      this.showOtherConsoleVersion = false;
    }
    if (!isInit) {
      // setter le format prefere pour la version de la console
      if (consoleVersion.consoleFormatType && consoleVersion.consoleFormatType.id) {
        this.vraData.consoleFormatType.id = consoleVersion.consoleFormatType.id;
      } else {
        this.vraData.consoleFormatType.id = null;
      }
    }

    this.isSuggestedConsoleSet = false;
  }

  onChangeConsoleOtherVersion(consoleVersionId: any, isInit = false) {
    this.isSuggestedConsoleSet = false;
  }

  onChangeConsoleFormat(consoleVersionId: any, isInit = false) {
    this.isSuggestedConsoleSet = false;
  }

  handleGetError(error: any) {
    console.error(error);
    showErrorMessage(this.modal, this.i18nTranslator.translate('error.generic'));
  }
}
